import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import RadioGroupField from 'components/RadioGroupField';
import { getSortComparator } from 'utils';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  CreateArchiveFormValues,
  DATA_ROOM_ARCHIVE_FILE_TYPE,
  DataRoomArchive,
} from '../../../types';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './CreateIndexForm.styles.scss';

export type CreateIndexFormProps = {
  className?: string;
  buySideGroups?: DataRoomBuySideGroup[];
  addArchive: (archive: DataRoomArchive) => void;
  onClose?: () => void;
};

const CreateIndexForm: React.FC<CreateIndexFormProps> = (props) => {
  const { className, buySideGroups, addArchive, onClose } = props;

  const {
    dataRoom: { _id },
    doesntHaveAccess,
  } = useContext(DataRoomContext);

  const classes = classNames('create-index-form', className);

  const { t } = useTranslation();

  const fileTypeOptions = useCreateSelectOptions()(
    Object.values(DATA_ROOM_ARCHIVE_FILE_TYPE),
  );

  const hasBuySideGroups = !!buySideGroups?.length;

  const buySideOptions = useMemo(
    () =>
      (buySideGroups || [])
        .sort((a, b) => getSortComparator(a.name, b.name))
        .map(({ _id, name }) => ({
          value: _id,
          label: name,
        })),
    [buySideGroups],
  );

  const initialValues = useMemo(
    () => ({
      fileType: fileTypeOptions?.[0]?.value,
      ...(hasBuySideGroups && {
        dataRoomBuySideGroupId: buySideOptions?.[0]?.value,
      }),
    }),
    [buySideOptions, fileTypeOptions, hasBuySideGroups],
  );

  const renderCheckbox = useCallback(
    (name: string, label: string) => (
      <Field
        name={name}
        label={t(label)}
        type="checkbox"
        component={CheckboxField}
      />
    ),
    [t],
  );

  const validate = useCallback(
    ({
      hasFiles,
      hasActivityLogs,
      hasQA,
      hasMembers,
    }: CreateArchiveFormValues) => {
      const hasAnySelection =
        hasFiles || hasActivityLogs || hasQA || hasMembers;

      return hasAnySelection
        ? {}
        : { dataSelection: t('chooseAtLeastOneOption') };
    },
    [t],
  );

  const [generateArchive] = useCallbackAsync(
    async (values: CreateArchiveFormValues) => {
      if (doesntHaveAccess) return;

      const { data } = await dataRoomApi.archives.createArchive(_id, values);

      addArchive(data);
      onClose?.();
    },
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={generateArchive}
      validate={validate}
      render={({ submitting, invalid, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          {hasBuySideGroups && (
            <>
              <p className="subtitle">
                {t('createDRIndexForBuyGroupDescription')}
              </p>
              <RadioGroupField
                name="dataRoomBuySideGroupId"
                options={buySideOptions}
              />
            </>
          )}

          <p className="subtitle">{t('chooseDataYouWouldLikeToStore')}</p>
          <div className="checkbox-wrapper">
            {renderCheckbox('hasFiles', 'files')}
            {renderCheckbox('hasActivityLogs', 'activityLog')}
            {renderCheckbox('hasQA', 'qAndA')}
            {renderCheckbox('hasMembers', 'members')}
          </div>

          <p className="subtitle">{t('chooseFileTypeForLogs')}</p>
          <RadioGroupField name="fileType" options={fileTypeOptions} />

          {hasBuySideGroups && (
            <>
              <p className="subtitle">{t('shareTheIndex')}</p>
              {renderCheckbox('shareIndex', 'shareIndexWithBuySideGroup')}
            </>
          )}

          <p className="subtitle">{t('downloadIndex')}</p>
          {renderCheckbox('downloadIndex', 'downlaodIndexAutomatically')}

          <p className="subtitle">{t('fileSecurity')}</p>
          {renderCheckbox('turnOffProtections', 'turnOffAllFileProtection')}

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button type="submit" disabled={submitting || invalid}>
              {t('generateIndex')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default CreateIndexForm;
